import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useMutation, useQuery } from "convex/react";
import { App as CapacitorApp } from "@capacitor/app";
import { ChevronLeft, ChevronRight, Settings, ShieldAlert } from "lucide-react";
import { Link, useLocation } from "react-router";
import { Capacitor } from "@capacitor/core";
import { AnimatePresence } from "motion/react";

import { AppIcon } from "../../components/apps/AppIcon";
import {
  TAB_BODY_COMPACT_TIMEFRAME_CLASS,
  TabPageHeader,
} from "../../components/navigation/TabPageHeader";
import { TabPageShell } from "../../components/navigation/TabPageShell";
import { Screen0Ring, type RingMetric, type RingSegment } from "../../components/screen0-ring/Screen0Ring";
import { SegmentFocusCard } from "../../components/screen0-ring/SegmentFocusCard";
import {
  isPrimaryPointerButton,
  shouldDismissTodayFocusFromPointerTarget,
} from "../../components/screen0-ring/screen0RingFocus";
import { FocusSeriesChart, type FocusSeriesPoint } from "../../components/screen0-ring/FocusSeriesChart";
import { AppDetailDrawer } from "../habits/AppDetailDrawer";
import { BottomSheet } from "../habits/BottomSheet";
import { QueryError } from "../../components/query/QueryStatus";
import { assignBrandAppColors } from "../../lib/apps/appUsageColors";
import { assignCategoryEntityColors } from "../../lib/apps/categoryColors";
import { AppFriendlyName } from "../../lib/apps/AppFriendlyName";
import {
  addDays,
  addMonths,
  canNavigateDayNext,
  canNavigateDayPrev,
  canNavigateMonthNext,
  canNavigateMonthPrev,
  canNavigateWeekNext,
  canNavigateWeekPrev,
  startOfMonth,
  startOfWeek,
} from "../../lib/dateNav";
import { formatActivationCountForDisplay, formatDuration, todayKey } from "../../lib/format";
import { TodayHeaderToggles } from "./TodayHeaderToggles";
import { PeriodMenu } from "./PeriodMenu";
import { mutationRef, queryRef } from "../../lib/convex/refs";
import { resolveQueryState } from "../../lib/convex/useQueryState";
import { useDeviceId } from "../../lib/device/DeviceProvider";
import { useMarketingDemoOverlay } from "../../lib/marketing/MarketingDemoProvider";
import { triggerLightTabHaptic, triggerSelectionChangeHaptic } from "../../lib/native/haptics";
import { canRequestUsagePermission, resolveLivePermission, useUsageAccess } from "../../lib/native/usageAccess";
import { openAndroidAppInfo } from "../../lib/native/usage";
import { TodayPeriodPager, type PeriodPagerNav } from "./TodayPeriodPager";
import { TodayTitleCalendar } from "./TodayTitleCalendar";
import { DailyAverageToggle } from "./DailyAverageToggle";
import {
  applyDailyAverageToPeriodData,
  resolveDaysWithData,
  shouldApplyDailyAverage,
} from "./todayDailyAverage";
import { appCategoryDisplayLabel } from "../../lib/apps/categoryIcons";
import { useLongPress } from "../../hooks/useLongPress";
import { resolvePanelOverview, shouldKeepTodayLoadingShell } from "./todayOverviewState";
import {
  isTodayPanelLoading,
  resolveRingTotal,
  RING_DRAW_MS,
  shouldPlayRingDrawOnReveal,
} from "./todayRingAnimation";
import type { ViewMode } from "../../lib/habits/heatmap";
import {
  aggregateTodayEntities,
  todayEntityLabel,
  type TodayEntity,
} from "./todayAggregation";

import {
  resolveFocusedEntity,
  resolveRingSegmentIndexForEntityKey,
} from "./todayFocusSelection";
import { isPreInstallationPeriod } from "./preInstallPeriod";
import {
  isPermissionGrantTransition,
  nextPostGrantRetryDelayMs,
  type PostGrantCollectResultLike,
} from "./postGrantCollect";
import { isUnattributedApp } from "../../lib/native/mergeSameDaySnapshot";
import { useScreen0Language } from "@/features/language";
import { formatTemplate, getTranslations, type TranslationStructure } from "@/i18n";

type TopApp = {
  appIdentifier?: string;
  displayName: string;
  durationSeconds: number;
  category?: string;
  isEstimated?: boolean;
  color?: string;
  opens?: number;
};

type Comparison = {
  previousDate?: string;
  previousTotalScreenTimeSeconds?: number;
  deltaSeconds?: number;
  previousTotalOpens?: number;
  deltaOpens?: number;
  direction: "up" | "down" | "flat" | "unavailable";
  opensDirection?: "up" | "down" | "flat" | "unavailable";
};

type DayOverview = {
  today: {
    date: string;
    totalScreenTimeSeconds: number | null;
    estimatedUnlockCount: number | null;
    unlockCountUnavailableReason?: string;
    permissionStatus: "granted" | "denied" | "unavailable";
    collectionStatus: "success" | "missing_permission" | "partial" | "native_error" | "metric_unsupported" | "no_data";
    reason?: string;
    source: "ios" | "android" | "web-mock";
    topApps: TopApp[];
    timeBands: Array<{ key: string; seconds: number; opens?: number }>;
    capturedAt: number;
  } | null;
  comparison: Comparison;
};

type WeekOverview = {
  week: {
    weekStart: string;
    weekEnd: string;
    anchorDate: string;
    totalScreenTimeSeconds: number;
    estimatedUnlockCount: number | null;
    unlockCountUnavailableReason?: string;
    permissionStatus: "granted" | "denied" | "unavailable";
    collectionStatus: "success" | "missing_permission" | "partial" | "native_error" | "metric_unsupported" | "no_data";
    reason?: string;
    source: "ios" | "android" | "web-mock";
    topApps: TopApp[];
    timeBands: Array<{ key: string; seconds: number; opens?: number }>;
    daysWithData: number;
    capturedAt: number;
  };
  comparison: Comparison;
};

type MonthOverview = {
  month: {
    monthStart: string;
    monthEnd: string;
    anchorDate: string;
    totalScreenTimeSeconds: number;
    estimatedUnlockCount: number | null;
    unlockCountUnavailableReason?: string;
    permissionStatus: "granted" | "denied" | "unavailable";
    collectionStatus: "success" | "missing_permission" | "partial" | "native_error" | "metric_unsupported" | "no_data";
    reason?: string;
    source: "ios" | "android" | "web-mock";
    topApps: TopApp[];
    timeBands: Array<{ key: string; seconds: number; opens?: number }>;
    daysWithData: number;
    capturedAt: number;
  };
  comparison: Comparison;
};

type PeriodMode = "day" | "week" | "month";

type PeriodOverview = DayOverview | WeekOverview | MonthOverview;

// Native collect + sync costs ~100ms; keep this short so foreground resumes and
// tab returns reliably refresh today's total instead of leaving it stale.
const COLLECT_DEBOUNCE_MS = 15_000;
/** Matches Habits/Insight shell gap; used between Today command bar and ring/sections. */
const TODAY_PANEL_GAP_CLASS = "grid gap-3";
/** Swipe hit target and horizontal track width for the ring pager only. */
const RING_PAGER_VIEWPORT_CLASS = "relative mx-auto aspect-square w-full max-w-[360px]";

const prefsRef = queryRef<
  { anonymousDeviceId: string },
  { todayRingMetric: RingMetric; todayViewMode: ViewMode } | null
>("habitPrefs:getPrefs");
const upsertPrefsRef = mutationRef<
  {
    anonymousDeviceId: string;
    patch: { todayRingMetric?: RingMetric; todayViewMode?: ViewMode };
  },
  null
>("habitPrefs:upsertPrefs");

export function TodayTab({ onCollectUsage }: { onCollectUsage?: () => Promise<unknown> }) {
  const { languageCode } = useScreen0Language();
  const t = getTranslations(languageCode);
  const anonymousDeviceId = useDeviceId();
  const { permission: livePermission, permissionReady, requiresEntitlement } = useUsageAccess();
  const lastCollectAt = useRef(0);
  const dayCacheRef = useRef(new Map<string, DayOverview>());
  const weekCacheRef = useRef(new Map<string, WeekOverview>());
  const monthCacheRef = useRef(new Map<string, MonthOverview>());
  const [hasEverLoaded, setHasEverLoaded] = useState(false);
  const [mode, setMode] = useState<PeriodMode>("day");
  const [selectedDate, setSelectedDate] = useState(() => todayKey());
  const [focusedEntityKey, setFocusedEntityKey] = useState<string | null>(null);
  const [detailEntity, setDetailEntity] = useState<TodayEntity | null>(null);
  const [allEntitiesDrawer, setAllEntitiesDrawer] = useState<{
    entities: TodayEntity[];
    metric: RingMetric;
    viewMode: ViewMode;
    averageMode: boolean;
  } | null>(null);
  const [showDailyAverage, setShowDailyAverage] = useState(false);
  const [animateRingDraw, setAnimateRingDraw] = useState(false);
  const [ringMetric, setRingMetric] = useState<RingMetric>("time");
  const [todayViewMode, setTodayViewMode] = useState<ViewMode>("app");
  const [isCollectingToday, setIsCollectingToday] = useState(false);
  const [isPostGrantCollecting, setIsPostGrantCollecting] = useState(false);
  const postGrantAttemptRef = useRef(0);
  const postGrantTimerRef = useRef<number | null>(null);
  const prevLivePermissionStatusRef = useRef<string | null>(null);
  const prefsHydrated = useRef(false);
  const panelWasLoadingRef = useRef(true);
  const suppressRevealAnimationRef = useRef(false);
  const pagerNavRef = useRef<PeriodPagerNav>({ goPrev: () => {}, goNext: () => {} });
  const pendingTodayCollectsRef = useRef(0);
  const referenceDate = todayKey();
  // Session start used to detect pre-launch "today" snapshots via their capturedAt.
  // Set once on cold start (TodayTab is kept-alive across tab switches).
  const todayCollectStartedAtRef = useRef(Date.now());
  const [todayFreshnessEpoch, setTodayFreshnessEpoch] = useState(0);
  const initialTodaySessionMarkedRef = useRef(false);

  const markTodayFreshSession = useCallback(() => {
    todayCollectStartedAtRef.current = Date.now();
    setTodayFreshnessEpoch((e) => e + 1);
  }, []);

  const isViewingToday = mode === "day" && selectedDate === referenceDate;
  const periodStep = mode === "day" ? 1 : 7;

  // Reset ring focus when navigating to a different date, switching modes, or changing view grouping
  useEffect(() => {
    setFocusedEntityKey(null);
  }, [mode, selectedDate, todayViewMode]);

  useEffect(() => {
    if (!animateRingDraw) return;
    const timer = window.setTimeout(() => setAnimateRingDraw(false), RING_DRAW_MS);
    return () => window.clearTimeout(timer);
  }, [animateRingDraw]);

  const dayRawReal = useQuery(
    queryRef<{ anonymousDeviceId: string; date: string }, DayOverview>("usage:getOverview"),
    { anonymousDeviceId, date: selectedDate },
  );
  const weekRawReal = useQuery(
    queryRef<{ anonymousDeviceId: string; anchorDate: string }, WeekOverview>("usage:getWeekOverview"),
    { anonymousDeviceId, anchorDate: selectedDate },
  );
  const monthRawReal = useQuery(
    queryRef<{ anonymousDeviceId: string; anchorDate: string }, MonthOverview>("usage:getMonthOverview"),
    mode === "month" ? { anonymousDeviceId, anchorDate: selectedDate } : "skip",
  );
  const dayRaw = useMarketingDemoOverlay(
    "usage:getOverview",
    { anonymousDeviceId, date: selectedDate },
    dayRawReal,
  );
  const weekRaw = useMarketingDemoOverlay(
    "usage:getWeekOverview",
    { anonymousDeviceId, anchorDate: selectedDate },
    weekRawReal,
  );
  const monthRaw = useMarketingDemoOverlay(
    "usage:getMonthOverview",
    mode === "month" ? { anonymousDeviceId, anchorDate: selectedDate } : "skip",
    monthRawReal,
  );
  const brandColorMap = useQuery(
    queryRef<Record<string, never>, Record<string, string>>("appBrandColors:getMap"),
    {},
  );
  const earliestDataRawReal = useQuery(
    queryRef<{ anonymousDeviceId: string }, { earliestDataDate: string | null }>("usage:getEarliestDataDate"),
    { anonymousDeviceId },
  );
  const earliestDataRaw = useMarketingDemoOverlay(
    "usage:getEarliestDataDate",
    { anonymousDeviceId },
    earliestDataRawReal,
  );
  const earliestDataDate = earliestDataRaw?.earliestDataDate ?? null;
  const prefs = useQuery(prefsRef, { anonymousDeviceId });
  const upsertPrefs = useMutation(upsertPrefsRef);

  useEffect(() => {
    if (prefs === undefined || prefsHydrated.current) return;
    prefsHydrated.current = true;
    if (prefs?.todayRingMetric) {
      setRingMetric(prefs.todayRingMetric);
    }
    if (prefs?.todayViewMode) {
      setTodayViewMode(prefs.todayViewMode);
    }
  }, [prefs]);

  const queryState = useMemo(
    () => resolveQueryState(mode === "day" ? dayRaw : mode === "week" ? weekRaw : monthRaw),
    [dayRaw, mode, weekRaw, monthRaw],
  );

  useEffect(() => {
    if (dayRaw === undefined) return;
    dayCacheRef.current.set(selectedDate, dayRaw);
    setHasEverLoaded(true);
  }, [dayRaw, selectedDate]);

  useEffect(() => {
    if (weekRaw === undefined) return;
    weekCacheRef.current.set(selectedDate, weekRaw);
    setHasEverLoaded(true);
  }, [weekRaw, selectedDate]);

  useEffect(() => {
    if (monthRaw === undefined) return;
    monthCacheRef.current.set(selectedDate, monthRaw);
    setHasEverLoaded(true);
  }, [monthRaw, selectedDate]);

  const canGoPrev = mode === "day"
    ? canNavigateDayPrev(selectedDate, referenceDate)
    : mode === "week"
      ? canNavigateWeekPrev(selectedDate, referenceDate)
      : canNavigateMonthPrev(selectedDate, referenceDate);
  const canGoNext = mode === "day"
    ? canNavigateDayNext(selectedDate, referenceDate)
    : mode === "week"
      ? canNavigateWeekNext(selectedDate, referenceDate)
      : canNavigateMonthNext(selectedDate, referenceDate);
  const stepDate = (date: string, direction: -1 | 1) =>
    mode === "month"
      ? startOfMonth(addMonths(date, direction))
      : addDays(date, direction * periodStep);
  const prevDate = canGoPrev ? stepDate(selectedDate, -1) : null;
  const nextDate = canGoNext ? stepDate(selectedDate, 1) : null;

  const prevDayRawReal = useQuery(
    queryRef<{ anonymousDeviceId: string; date: string }, DayOverview>("usage:getOverview"),
    mode === "day" && prevDate ? { anonymousDeviceId, date: prevDate } : "skip",
  );
  const nextDayRawReal = useQuery(
    queryRef<{ anonymousDeviceId: string; date: string }, DayOverview>("usage:getOverview"),
    mode === "day" && nextDate ? { anonymousDeviceId, date: nextDate } : "skip",
  );
  const prevWeekRawReal = useQuery(
    queryRef<{ anonymousDeviceId: string; anchorDate: string }, WeekOverview>("usage:getWeekOverview"),
    mode === "week" && prevDate ? { anonymousDeviceId, anchorDate: prevDate } : "skip",
  );
  const nextWeekRawReal = useQuery(
    queryRef<{ anonymousDeviceId: string; anchorDate: string }, WeekOverview>("usage:getWeekOverview"),
    mode === "week" && nextDate ? { anonymousDeviceId, anchorDate: nextDate } : "skip",
  );
  const prevMonthRawReal = useQuery(
    queryRef<{ anonymousDeviceId: string; anchorDate: string }, MonthOverview>("usage:getMonthOverview"),
    mode === "month" && prevDate ? { anonymousDeviceId, anchorDate: prevDate } : "skip",
  );
  const nextMonthRawReal = useQuery(
    queryRef<{ anonymousDeviceId: string; anchorDate: string }, MonthOverview>("usage:getMonthOverview"),
    mode === "month" && nextDate ? { anonymousDeviceId, anchorDate: nextDate } : "skip",
  );
  const prevDayRaw = useMarketingDemoOverlay(
    "usage:getOverview",
    mode === "day" && prevDate ? { anonymousDeviceId, date: prevDate } : "skip",
    prevDayRawReal,
  );
  const nextDayRaw = useMarketingDemoOverlay(
    "usage:getOverview",
    mode === "day" && nextDate ? { anonymousDeviceId, date: nextDate } : "skip",
    nextDayRawReal,
  );
  const prevWeekRaw = useMarketingDemoOverlay(
    "usage:getWeekOverview",
    mode === "week" && prevDate ? { anonymousDeviceId, anchorDate: prevDate } : "skip",
    prevWeekRawReal,
  );
  const nextWeekRaw = useMarketingDemoOverlay(
    "usage:getWeekOverview",
    mode === "week" && nextDate ? { anonymousDeviceId, anchorDate: nextDate } : "skip",
    nextWeekRawReal,
  );
  const prevMonthRaw = useMarketingDemoOverlay(
    "usage:getMonthOverview",
    mode === "month" && prevDate ? { anonymousDeviceId, anchorDate: prevDate } : "skip",
    prevMonthRawReal,
  );
  const nextMonthRaw = useMarketingDemoOverlay(
    "usage:getMonthOverview",
    mode === "month" && nextDate ? { anonymousDeviceId, anchorDate: nextDate } : "skip",
    nextMonthRawReal,
  );

  const goPrev = useCallback(() => {
    if (!canGoPrev) return;
    suppressRevealAnimationRef.current = true;
    setAnimateRingDraw(false);
    setSelectedDate((current) =>
      mode === "month" ? startOfMonth(addMonths(current, -1)) : addDays(current, mode === "day" ? -1 : -7),
    );
  }, [canGoPrev, mode]);

  const goNext = useCallback(() => {
    if (!canGoNext) return;
    suppressRevealAnimationRef.current = true;
    setAnimateRingDraw(false);
    setSelectedDate((current) =>
      mode === "month" ? startOfMonth(addMonths(current, 1)) : addDays(current, mode === "day" ? 1 : 7),
    );
  }, [canGoNext, mode]);

  const handleModeChange = useCallback((nextMode: PeriodMode) => {
    if (nextMode === mode) return;
    triggerSelectionChangeHaptic();
    setMode(nextMode);
    setAnimateRingDraw(true);
  }, [mode]);

  const tryCollectUsage = useCallback(
    (bypassDebounce = false) => {
      const now = Date.now();
      if (!bypassDebounce && now - lastCollectAt.current < COLLECT_DEBOUNCE_MS) return;
      lastCollectAt.current = now;
      if (!onCollectUsage) return;
      pendingTodayCollectsRef.current += 1;
      setIsCollectingToday(true);
      void onCollectUsage().finally(() => {
        pendingTodayCollectsRef.current = Math.max(0, pendingTodayCollectsRef.current - 1);
        if (pendingTodayCollectsRef.current === 0) {
          setIsCollectingToday(false);
        }
      });
    },
    [onCollectUsage],
  );

  // Post-grant collect loop: run immediately (debounce bypassed) when live
  // permission transitions to granted, then retry on a short backoff while the
  // OS reports granted but still returns zero usage (OEM propagation lag).
  const runPostGrantCollect = useCallback(() => {
    if (!onCollectUsage) return;
    lastCollectAt.current = Date.now();
    pendingTodayCollectsRef.current += 1;
    setIsCollectingToday(true);
    setIsPostGrantCollecting(true);
    onCollectUsage()
      .then((result) => {
        const delay = nextPostGrantRetryDelayMs(
          result as PostGrantCollectResultLike,
          postGrantAttemptRef.current,
        );
        if (delay === null) {
          postGrantAttemptRef.current = 0;
          setIsPostGrantCollecting(false);
          return;
        }
        postGrantAttemptRef.current += 1;
        postGrantTimerRef.current = window.setTimeout(() => {
          postGrantTimerRef.current = null;
          runPostGrantCollect();
        }, delay);
      })
      .catch(() => {
        postGrantAttemptRef.current = 0;
        setIsPostGrantCollecting(false);
      })
      .finally(() => {
        pendingTodayCollectsRef.current = Math.max(0, pendingTodayCollectsRef.current - 1);
        if (pendingTodayCollectsRef.current === 0) {
          setIsCollectingToday(false);
        }
      });
  }, [onCollectUsage]);

  useEffect(() => {
    if (!permissionReady) return;
    const status = livePermission?.status ?? null;
    const previous = prevLivePermissionStatusRef.current;
    prevLivePermissionStatusRef.current = status;
    if (!isPermissionGrantTransition(previous, status)) return;
    if (postGrantTimerRef.current !== null) {
      window.clearTimeout(postGrantTimerRef.current);
      postGrantTimerRef.current = null;
    }
    postGrantAttemptRef.current = 0;
    runPostGrantCollect();
  }, [livePermission, permissionReady, runPostGrantCollect]);

  useEffect(() => () => {
    if (postGrantTimerRef.current !== null) {
      window.clearTimeout(postGrantTimerRef.current);
      postGrantTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!isViewingToday) return;
    // Session epoch is for cold-start pre-launch snapshot filtering only — mark once.
    if (!initialTodaySessionMarkedRef.current) {
      initialTodaySessionMarkedRef.current = true;
      markTodayFreshSession();
    }
    tryCollectUsage(true);
  }, [isViewingToday, tryCollectUsage, markTodayFreshSession]);

  // TodayTab stays mounted in KeepAliveTabPanels, so returning to this tab does
  // not remount it; re-collect (debounced) when it becomes the active tab again.
  const isActiveTab = useLocation().pathname === "/today";
  useEffect(() => {
    if (!isActiveTab || !isViewingToday) return;
    tryCollectUsage(false);
  }, [isActiveTab, isViewingToday, tryCollectUsage]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform() || !isViewingToday) return;

    const listener = CapacitorApp.addListener("appStateChange", (event) => {
      if (!event.isActive) return;
      tryCollectUsage(false);
    });

    return () => {
      void listener.then((handle) => handle.remove());
    };
  }, [isViewingToday, tryCollectUsage, markTodayFreshSession]);

  const bindPagerNav = useCallback((nav: PeriodPagerNav) => {
    pagerNavRef.current = nav;
  }, []);

  const headerNav = useMemo(
    () => ({
      onPrev: () => pagerNavRef.current.goPrev(),
      onNext: () => pagerNavRef.current.goNext(),
    }),
    [],
  );

  const handleEntityLongPress = useCallback((entity: TodayEntity) => {
    setFocusedEntityKey(null);
    setDetailEntity(entity);
  }, []);

  const applyMetricChange = useCallback(
    (next: RingMetric) => {
      if (next === ringMetric) return;
      triggerSelectionChangeHaptic();
      setRingMetric(next);
      setAnimateRingDraw(true);
      void upsertPrefs({ anonymousDeviceId, patch: { todayRingMetric: next } });
    },
    [anonymousDeviceId, ringMetric, upsertPrefs],
  );

  const applyViewModeChange = useCallback(
    (next: ViewMode) => {
      if (next === todayViewMode) return;
      triggerSelectionChangeHaptic();
      setTodayViewMode(next);
      setFocusedEntityKey(null);
      setAnimateRingDraw(true);
      void upsertPrefs({ anonymousDeviceId, patch: { todayViewMode: next } });
    },
    [anonymousDeviceId, todayViewMode, upsertPrefs],
  );

  const handleRingMetricToggle = useCallback(() => {
    applyMetricChange(ringMetric === "time" ? "opens" : "time");
  }, [applyMetricChange, ringMetric]);

  const handleTodayViewModeToggle = useCallback(() => {
    applyViewModeChange(todayViewMode === "app" ? "category" : "app");
  }, [applyViewModeChange, todayViewMode]);

  const handleDailyAverageToggle = useCallback(() => {
    setShowDailyAverage((current) => !current);
  }, []);

  const handleMetricSelect = useCallback(
    (next: RingMetric) => {
      applyMetricChange(next);
    },
    [applyMetricChange],
  );

  const currentRaw = mode === "day" ? dayRaw : mode === "week" ? weekRaw : monthRaw;
  const prevRaw = mode === "day" ? prevDayRaw : mode === "week" ? prevWeekRaw : prevMonthRaw;
  const nextRaw = mode === "day" ? nextDayRaw : mode === "week" ? nextWeekRaw : nextMonthRaw;

  const resolvePanelContext = (
    date: string,
    raw: PeriodOverview | undefined,
  ) => {
    const panelIsViewingToday = mode === "day" && date === referenceDate;
    const cache = mode === "day" ? dayCacheRef.current : mode === "week" ? weekCacheRef.current : monthCacheRef.current;
    const keepLoadingShell = mode === "day" && shouldKeepTodayLoadingShell(
      raw as DayOverview | undefined,
      cache.get(date) as DayOverview | undefined,
      panelIsViewingToday,
      isCollectingToday,
      todayCollectStartedAtRef.current,
    );
    const panelState = resolvePanelOverview(raw, cache.get(date), hasEverLoaded);
    return { panelIsViewingToday, keepLoadingShell, panelState };
  };

  const buildRingPanel = (
    date: string,
    raw: PeriodOverview | undefined,
    options: { segmentSelection: boolean; animateRingDraw: boolean },
  ) => {
    const { panelIsViewingToday, keepLoadingShell, panelState } = resolvePanelContext(date, raw);

    if (keepLoadingShell || panelState.status === "initial-loading") {
      return <RingAreaSkeleton />;
    }
    if (panelState.status === "shell") {
      return <RingAreaSkeleton />;
    }

    return (
      <TodayRingViewport
        mode={mode}
        selectedDate={date}
        overview={panelState.overview}
        brandColorMap={brandColorMap}
        livePermission={livePermission}
        permissionReady={permissionReady}
        isViewingToday={panelIsViewingToday}
        focusedEntityKey={options.segmentSelection ? focusedEntityKey : null}
        onFocusedEntityKeyChange={options.segmentSelection ? setFocusedEntityKey : undefined}
        isRefreshing={panelState.isRefreshing}
        animateRingDraw={options.animateRingDraw}
        ringMetric={ringMetric}
        todayViewMode={todayViewMode}
        showDailyAverage={showDailyAverage}
        onRingMetricToggle={options.segmentSelection ? handleRingMetricToggle : undefined}
        onMetricSelect={options.segmentSelection ? handleMetricSelect : undefined}
        earliestDataDate={earliestDataDate}
        onCollectUsage={onCollectUsage}
        isPostGrantCollecting={isPostGrantCollecting}
      />
    );
  };

  const renderFixedSections = () => {
    const { keepLoadingShell, panelState } = resolvePanelContext(selectedDate, currentRaw);

    if (keepLoadingShell || panelState.status === "initial-loading") {
      return <TodayFixedShell mode={mode} selectedDate={selectedDate} isRefreshing />;
    }
    if (panelState.status === "shell") {
      return <TodayFixedShell mode={mode} selectedDate={selectedDate} isRefreshing={panelState.isRefreshing} />;
    }

    return (
      <TodayPeriodFixedSections
        mode={mode}
        selectedDate={selectedDate}
        overview={panelState.overview}
        brandColorMap={brandColorMap}
        livePermission={livePermission}
        permissionReady={permissionReady}
        focusedEntityKey={focusedEntityKey}
        onFocusedEntityKeyChange={setFocusedEntityKey}
        isRefreshing={panelState.isRefreshing}
        onEntityLongPress={handleEntityLongPress}
        onViewMore={setAllEntitiesDrawer}
        ringMetric={ringMetric}
        todayViewMode={todayViewMode}
        showDailyAverage={showDailyAverage}
        onMetricSelect={handleMetricSelect}
      />
    );
  };

  const currentCache = mode === "day" ? dayCacheRef.current : mode === "week" ? weekCacheRef.current : monthCacheRef.current;
  const currentPanelState = resolvePanelOverview(
    currentRaw,
    currentCache.get(selectedDate),
    hasEverLoaded,
  );
  const toggleDaysWithData = useMemo(() => {
    if (mode !== "week" && mode !== "month") return 0;
    if (currentPanelState.status !== "content") return 0;
    return resolveDaysWithData(mode, currentPanelState.overview);
  }, [currentPanelState, mode]);
  const currentKeepLoadingShell = mode === "day" && shouldKeepTodayLoadingShell(
    currentRaw as DayOverview | undefined,
    dayCacheRef.current.get(selectedDate) as DayOverview | undefined,
    isViewingToday,
    isCollectingToday,
    todayCollectStartedAtRef.current,
  );
  // todayFreshnessEpoch bump (from resume/mark) forces this render path to re-read
  // the updated ref and potentially flip keepLoadingShell for stale today snapshots.
  void todayFreshnessEpoch;
  const currentPanelLoading = isTodayPanelLoading(currentKeepLoadingShell, currentPanelState);

  useLayoutEffect(() => {
    const wasLoading = panelWasLoadingRef.current;
    panelWasLoadingRef.current = currentPanelLoading;

    if (suppressRevealAnimationRef.current) {
      suppressRevealAnimationRef.current = false;
      return;
    }

    if (!wasLoading || currentPanelLoading || currentPanelState.status !== "content") {
      return;
    }

    const ringTotal = resolveRingTotal(currentPanelState.overview, mode, ringMetric);
    if (shouldPlayRingDrawOnReveal(ringTotal)) {
      setAnimateRingDraw(true);
    }
  }, [currentPanelLoading, currentPanelState, mode, ringMetric]);

  const handleCalendarSelect = useCallback((date: string) => {
    suppressRevealAnimationRef.current = true;
    setAnimateRingDraw(false);
    setSelectedDate(date);
  }, []);

  const showTopLevelError = queryState.status === "error" && currentPanelState.status === "initial-loading";

  return (
    <TabPageShell
      className={TAB_BODY_COMPACT_TIMEFRAME_CLASS}
      fixed={
        <TabPageHeader
          titleSlot={
            <TodayTitleCalendar
              mode={mode}
              selectedDate={selectedDate}
              referenceDate={referenceDate}
              onSelectDate={handleCalendarSelect}
            />
          }
          actions={
            <Link
              className="icon-button"
              to="/settings"
              aria-label={t.common.openSettings}
              onClick={() => triggerLightTabHaptic()}
            >
              <Settings size={20} />
            </Link>
          }
        />
      }
    >
      {requiresEntitlement ? (
        <section className="liquid-glass-alert-warning flex gap-3 p-4 text-[oklch(82%_0.055_85)]">
          <ShieldAlert size={20} />
          <p className="text-sm leading-6">{t.permission.entitlementMissing}</p>
        </section>
      ) : null}

      {showTopLevelError ? (
        <QueryError message={queryState.message} />
      ) : (
        <div className={TODAY_PANEL_GAP_CLASS}>
          <TodayHeader
            mode={mode}
            canGoPrev={canGoPrev}
            canGoNext={canGoNext}
            onPrev={headerNav.onPrev}
            onNext={headerNav.onNext}
            onModeChange={handleModeChange}
            ringMetric={ringMetric}
            todayViewMode={todayViewMode}
            onMetricToggle={handleRingMetricToggle}
            onViewModeToggle={handleTodayViewModeToggle}
          />
          <div className={RING_PAGER_VIEWPORT_CLASS}>
            {mode === "week" || mode === "month" ? (
              <DailyAverageToggle
                active={showDailyAverage}
                disabled={toggleDaysWithData <= 0}
                onToggle={handleDailyAverageToggle}
              />
            ) : null}
            <TodayPeriodPager
              className="absolute inset-0 overflow-hidden"
              gesturesDisabled={focusedEntityKey !== null}
              canGoPrev={canGoPrev}
              canGoNext={canGoNext}
              onCommitPrev={goPrev}
              onCommitNext={goNext}
              onBindNav={bindPagerNav}
              resetKey={`${mode}:${selectedDate}`}
              prevPanel={prevDate ? buildRingPanel(prevDate, prevRaw, { segmentSelection: false, animateRingDraw: false }) : <div />}
              currentPanel={buildRingPanel(selectedDate, currentRaw, { segmentSelection: true, animateRingDraw })}
              nextPanel={nextDate ? buildRingPanel(nextDate, nextRaw, { segmentSelection: false, animateRingDraw: false }) : <div />}
            />
          </div>
          {renderFixedSections()}
        </div>
      )}

      <AppDetailDrawer
        open={detailEntity !== null}
        onClose={() => setDetailEntity(null)}
        appIdentifier={detailEntity?.appIdentifier}
        displayName={detailEntity?.displayName ?? ""}
        category={detailEntity?.category}
        color={detailEntity?.color ?? "#7ab89a"}
        initialDate={selectedDate}
        isCategory={detailEntity?.isCategory}
      />

      <TopEntitiesAllDrawer
        drawer={allEntitiesDrawer}
        onClose={() => setAllEntitiesDrawer(null)}
      />
    </TabPageShell>
  );
}

function RingAreaSkeleton() {
  return (
    <div
      className="relative mx-auto aspect-square w-full max-w-[360px]"
      aria-hidden
    >
      <div className="absolute inset-[12%] rounded-full border-[24px] border-[oklch(28%_0.02_160)]" />
      <div className="absolute inset-0 grid place-items-center">
        <div className="h-12 w-20 rounded bg-[oklch(28%_0.02_160)]" />
      </div>
    </div>
  );
}

function TodayFixedShell({
  mode,
  selectedDate,
  isRefreshing,
}: {
  mode: PeriodMode;
  selectedDate: string;
  isRefreshing: boolean;
}) {
  return (
    <>
      <TopEntities entities={[]} metric="time" viewMode="app" showPlaceholder />
      <DayShapeChart selectedDate={selectedDate} mode={mode} metric="time" isRefreshing={isRefreshing} />
    </>
  );
}

type PeriodDisplayData = {
  hasUsage: boolean;
  permission: string;
  permissionReason?: string;
  totalSeconds: number;
  entities: TodayEntity[];
  ringEntities: TodayEntity[];
  segments: RingSegment[];
  entitiesTotalSeconds: number;
  entitiesTotalOpens: number;
  unlockCount: number | undefined;
  unlockUnavailableLabel: string | undefined;
};

function getPeriodDisplayData(
  mode: PeriodMode,
  overview: PeriodOverview,
  brandColorMap: Record<string, string> | undefined,
  livePermission: ReturnType<typeof useUsageAccess>["permission"],
  permissionReady: boolean,
  ringMetric: RingMetric,
  todayViewMode: ViewMode,
  todayCopy: TranslationStructure["today"],
  categories: TranslationStructure["categories"],
): PeriodDisplayData {
  const daySnapshot = mode === "day" ? (overview as DayOverview).today : null;
  const aggSnapshot = mode === "week"
    ? (overview as WeekOverview).week
    : mode === "month"
      ? (overview as MonthOverview).month
      : null;
  const permission = resolveLivePermission(
    permissionReady,
    livePermission,
    daySnapshot?.permissionStatus ?? aggSnapshot?.permissionStatus ?? "unavailable",
  );
  const permissionReason = livePermission?.reason ?? daySnapshot?.reason ?? aggSnapshot?.reason;
  const totalSeconds = mode === "day"
    ? daySnapshot?.totalScreenTimeSeconds ?? 0
    : aggSnapshot?.totalScreenTimeSeconds ?? 0;
  // The synthetic "Other" row reconciles the reported total against per-app sums
  // (see ScreenTimeTotalReconciliation) but iOS Settings never surfaces it as a
  // row — drop it here so it never leaks into the list, ring, or category views.
  // `totalSeconds` above is read directly from the snapshot's authoritative
  // total field and stays independent of this filtered list.
  const rawTopApps = assignBrandAppColors(
    (mode === "day" ? daySnapshot?.topApps ?? [] : aggSnapshot?.topApps ?? []).filter(
      (app) => !isUnattributedApp(app),
    ),
    brandColorMap,
    Number.POSITIVE_INFINITY,
  );
  const aggregated = aggregateTodayEntities(rawTopApps, todayViewMode, ringMetric);
  const entities = todayViewMode === "category"
    ? assignCategoryEntityColors(aggregated)
    : assignBrandAppColors(aggregated, brandColorMap, 5);
  const ringEntities = todayViewMode === "category"
    ? entities.filter((entity) =>
        ringMetric === "opens" ? (entity.opens ?? 0) > 0 : entity.durationSeconds > 0,
      ).slice(0, 5)
    : entities.slice(0, 5);
  const segments: RingSegment[] = ringEntities.map((entity) => ({
    segmentKey: entity.entityKey,
    name: entity.isCategory ? todayEntityLabel(entity, { categories }) : undefined,
    displayName: entity.displayName,
    appIdentifier: entity.appIdentifier,
    category: entity.category,
    isCategory: entity.isCategory,
    seconds: entity.durationSeconds,
    opens: entity.opens,
    color: entity.color!,
  }));
  // Ring denominators span the FULL entity population (not just the displayed
  // top 5) so the dark remainder = "all other apps" and the arcs never overfill
  // when the reported total under-counts the per-app sums (see ringGeometry).
  const entitiesTotalSeconds = entities.reduce((sum, entity) => sum + entity.durationSeconds, 0);
  const entitiesTotalOpens = entities.reduce((sum, entity) => sum + (entity.opens ?? 0), 0);
  const hasUsage = mode === "day"
    ? Boolean(daySnapshot && permission === "granted" && (totalSeconds > 0 || entities.length > 0))
    : Boolean(aggSnapshot && aggSnapshot.daysWithData > 0 && totalSeconds > 0);
  const unlockCount = mode === "day"
    ? daySnapshot?.estimatedUnlockCount ?? undefined
    : aggSnapshot?.estimatedUnlockCount ?? undefined;
  const unlockUnavailableLabel = unlockCount == null
    ? mode === "week"
      ? todayCopy.weeklyActivationsNotTracked
      : mode === "month"
        ? todayCopy.monthlyActivationsNotTracked
        : todayCopy.activationsNotTracked
    : undefined;

  return {
    hasUsage,
    permission,
    permissionReason,
    totalSeconds,
    entities,
    ringEntities,
    segments,
    entitiesTotalSeconds,
    entitiesTotalOpens,
    unlockCount,
    unlockUnavailableLabel,
  };
}

function resolvePeriodDisplayWithAverage(
  mode: PeriodMode,
  overview: PeriodOverview,
  showDailyAverage: boolean,
  brandColorMap: Record<string, string> | undefined,
  livePermission: ReturnType<typeof useUsageAccess>["permission"],
  permissionReady: boolean,
  ringMetric: RingMetric,
  todayViewMode: ViewMode,
  todayCopy: TranslationStructure["today"],
  categories: TranslationStructure["categories"],
): { data: PeriodDisplayData; averageMode: boolean } {
  const data = getPeriodDisplayData(
    mode,
    overview,
    brandColorMap,
    livePermission,
    permissionReady,
    ringMetric,
    todayViewMode,
    todayCopy,
    categories,
  );
  const daysWithData = resolveDaysWithData(mode, overview);
  const averageMode = shouldApplyDailyAverage(mode, showDailyAverage, daysWithData);
  return {
    data: averageMode ? applyDailyAverageToPeriodData(data, daysWithData) : data,
    averageMode,
  };
}

function TodayRingViewport({
  mode,
  selectedDate,
  overview,
  brandColorMap,
  livePermission,
  permissionReady,
  isViewingToday,
  focusedEntityKey,
  onFocusedEntityKeyChange,
  isRefreshing = false,
  animateRingDraw,
  ringMetric,
  todayViewMode,
  showDailyAverage,
  onRingMetricToggle,
  onMetricSelect,
  earliestDataDate,
  onCollectUsage,
  isPostGrantCollecting = false,
}: {
  mode: PeriodMode;
  selectedDate: string;
  overview: PeriodOverview;
  brandColorMap: Record<string, string> | undefined;
  livePermission: ReturnType<typeof useUsageAccess>["permission"];
  permissionReady: boolean;
  isViewingToday: boolean;
  focusedEntityKey: string | null;
  onFocusedEntityKeyChange?: (key: string | null) => void;
  isRefreshing?: boolean;
  animateRingDraw: boolean;
  ringMetric: RingMetric;
  todayViewMode: ViewMode;
  showDailyAverage: boolean;
  onRingMetricToggle?: () => void;
  onMetricSelect?: (metric: RingMetric) => void;
  earliestDataDate: string | null;
  onCollectUsage?: () => Promise<unknown>;
  isPostGrantCollecting?: boolean;
}) {
  const t = getTranslations(useScreen0Language().languageCode);
  const { data, averageMode } = resolvePeriodDisplayWithAverage(
    mode,
    overview,
    showDailyAverage,
    brandColorMap,
    livePermission,
    permissionReady,
    ringMetric,
    todayViewMode,
    t.today,
    t.categories,
  );

  if (!data.hasUsage) {
    return (
      <EmptyState
        mode={mode}
        permission={data.permission}
        reason={data.permissionReason}
        isViewingToday={isViewingToday}
        selectedDate={selectedDate}
        isRefreshing={isRefreshing}
        earliestDataDate={earliestDataDate}
        onCollectUsage={onCollectUsage}
        isPostGrantCollecting={isPostGrantCollecting}
      />
    );
  }

  const ringSegmentIndex = resolveRingSegmentIndexForEntityKey(data.entities, focusedEntityKey);
  const focusedEntity = resolveFocusedEntity(data.entities, focusedEntityKey);
  const focusedHasOpens = focusedEntity ? typeof focusedEntity.opens === "number" : true;

  const handleSegmentClick = (index: number | null) => {
    if (!onFocusedEntityKeyChange) return;
    if (index === null) {
      onFocusedEntityKeyChange(null);
      return;
    }
    onFocusedEntityKeyChange(data.ringEntities[index]!.entityKey);
  };

  const guardedMetricSelect = (next: RingMetric) => {
    if (next === ringMetric) return;
    if (next === "opens" && focusedEntityKey !== null && !focusedHasOpens) return;
    onMetricSelect?.(next);
  };

  const handleCenterMetric = () => {
    const next: RingMetric = ringMetric === "time" ? "opens" : "time";
    if (focusedEntityKey !== null) {
      guardedMetricSelect(next);
      return;
    }
    onRingMetricToggle?.();
  };

  return (
    <Screen0Ring
      segments={data.segments}
      totalSeconds={data.totalSeconds}
      totalOpens={data.unlockCount}
      entitiesTotalSeconds={data.entitiesTotalSeconds}
      entitiesTotalOpens={data.entitiesTotalOpens}
      metric={ringMetric}
      unlockCount={data.unlockCount}
      unlockUnavailableLabel={data.unlockUnavailableLabel}
      selectedIndex={ringSegmentIndex}
      onSegmentClick={onFocusedEntityKeyChange ? handleSegmentClick : undefined}
      onCenterClick={handleCenterMetric}
      isRefreshing={isRefreshing}
      animateDraw={animateRingDraw}
      averageMode={averageMode}
    />
  );
}

function TodayPeriodFixedSections({
  mode,
  selectedDate,
  overview,
  brandColorMap,
  livePermission,
  permissionReady,
  focusedEntityKey,
  onFocusedEntityKeyChange,
  isRefreshing = false,
  onEntityLongPress,
  onViewMore,
  ringMetric,
  todayViewMode,
  showDailyAverage,
  onMetricSelect,
}: {
  mode: PeriodMode;
  selectedDate: string;
  overview: PeriodOverview;
  brandColorMap: Record<string, string> | undefined;
  livePermission: ReturnType<typeof useUsageAccess>["permission"];
  permissionReady: boolean;
  focusedEntityKey: string | null;
  onFocusedEntityKeyChange: (key: string | null) => void;
  isRefreshing?: boolean;
  onEntityLongPress: (entity: TodayEntity) => void;
  onViewMore: (payload: {
    entities: TodayEntity[];
    metric: RingMetric;
    viewMode: ViewMode;
    averageMode: boolean;
  }) => void;
  ringMetric: RingMetric;
  todayViewMode: ViewMode;
  showDailyAverage: boolean;
  onMetricSelect: (metric: RingMetric) => void;
}) {
  const t = getTranslations(useScreen0Language().languageCode);
  const { data, averageMode } = resolvePeriodDisplayWithAverage(
    mode,
    overview,
    showDailyAverage,
    brandColorMap,
    livePermission,
    permissionReady,
    ringMetric,
    todayViewMode,
    t.today,
    t.categories,
  );

  if (!data.hasUsage) {
    return null;
  }

  const staleClass = isRefreshing ? "opacity-55 transition-opacity duration-200" : undefined;
  const focusedEntity = resolveFocusedEntity(data.entities, focusedEntityKey);
  const focusedHasOpens = focusedEntity ? typeof focusedEntity.opens === "number" : true;

  useEffect(() => {
    if (!focusedEntity) return;

    const onPointerUp = (event: PointerEvent) => {
      if (!isPrimaryPointerButton(event.button)) return;
      if (!shouldDismissTodayFocusFromPointerTarget(event.target)) return;
      onFocusedEntityKeyChange(null);
    };

    document.addEventListener("pointerup", onPointerUp);
    return () => document.removeEventListener("pointerup", onPointerUp);
  }, [focusedEntity, onFocusedEntityKeyChange]);

  const handleEntityTap = (index: number) => {
    const key = data.entities[index]!.entityKey;
    onFocusedEntityKeyChange(focusedEntityKey === key ? null : key);
  };

  const guardedMetricSelect = (next: RingMetric) => {
    if (next === ringMetric) return;
    if (next === "opens" && focusedEntityKey !== null && !focusedHasOpens) return;
    onMetricSelect(next);
  };

  return (
    <>
      <AnimatePresence>
        {focusedEntity ? (
          <SegmentFocusCard
            key="segment-focus"
            displayName={focusedEntity.displayName}
            appIdentifier={focusedEntity.appIdentifier}
            category={focusedEntity.category}
            color={focusedEntity.color!}
            durationSeconds={focusedEntity.durationSeconds}
            totalSeconds={data.totalSeconds}
            selectedDate={selectedDate}
            mode={mode}
            metric={ringMetric}
            opens={focusedEntity.opens}
            totalOpens={data.unlockCount}
            isCategory={focusedEntity.isCategory}
            averageMode={averageMode}
            onMetricSelect={guardedMetricSelect}
            onClose={() => onFocusedEntityKeyChange(null)}
          />
        ) : null}
      </AnimatePresence>
      <div className={`grid gap-3${staleClass ? ` ${staleClass}` : ""}`}>
        <TopEntities
          entities={data.entities}
          metric={ringMetric}
          viewMode={todayViewMode}
          averageMode={averageMode}
          onEntityTap={handleEntityTap}
          onEntityLongPress={onEntityLongPress}
          onViewMore={() =>
            onViewMore({ entities: data.entities, metric: ringMetric, viewMode: todayViewMode, averageMode })
          }
        />
        <DayShapeChart selectedDate={selectedDate} mode={mode} metric={ringMetric} />
      </div>
    </>
  );
}

type TodayHeaderProps = {
  mode: PeriodMode;
  canGoPrev: boolean;
  canGoNext: boolean;
  onPrev: () => void;
  onNext: () => void;
  onModeChange: (mode: PeriodMode) => void;
  ringMetric: RingMetric;
  todayViewMode: ViewMode;
  /** Provided only on the interactive (current) panel; absent panels render toggles inert. */
  onMetricToggle?: () => void;
  onViewModeToggle?: () => void;
};

function TodayHeader({
  mode,
  canGoPrev,
  canGoNext,
  onPrev,
  onNext,
  onModeChange,
  ringMetric,
  todayViewMode,
  onMetricToggle,
  onViewModeToggle,
}: TodayHeaderProps) {
  const t = getTranslations(useScreen0Language().languageCode);
  const navLabel = mode === "day" ? t.today.navDay : mode === "week" ? t.today.navWeek : t.today.navMonth;
  const togglesInteractive = onMetricToggle && onViewModeToggle;

  return (
    <div className="grid min-w-0 grid-cols-[1fr_auto_1fr] items-center gap-2">
      <div className="flex items-center justify-self-start gap-0.5">
        <button
          type="button"
          className="today-header-nav-button shrink-0"
          onClick={onPrev}
          disabled={!canGoPrev}
          aria-label={formatTemplate(t.today.prevNavAria, { period: navLabel })}
        >
          <ChevronLeft size={18} />
        </button>
        <button
          type="button"
          className="today-header-nav-button shrink-0"
          onClick={onNext}
          disabled={!canGoNext}
          aria-label={formatTemplate(t.today.nextNavAria, { period: navLabel })}
        >
          <ChevronRight size={18} />
        </button>
      </div>
      <div className={`justify-self-center ${togglesInteractive ? "" : "pointer-events-none"}`}>
        <TodayHeaderToggles
          viewMode={todayViewMode}
          onViewModeToggle={onViewModeToggle ?? (() => {})}
          metric={ringMetric}
          onMetricToggle={onMetricToggle ?? (() => {})}
        />
      </div>
      <div className="justify-self-end">
        <PeriodMenu mode={mode} onModeChange={onModeChange} />
      </div>
    </div>
  );
}

function EmptyState({
  mode,
  permission,
  reason,
  isViewingToday,
  selectedDate,
  isRefreshing = false,
  earliestDataDate,
  onCollectUsage,
  isPostGrantCollecting = false,
}: {
  mode: PeriodMode;
  permission: string;
  reason?: string;
  isViewingToday: boolean;
  selectedDate: string;
  isRefreshing?: boolean;
  earliestDataDate: string | null;
  onCollectUsage?: () => Promise<unknown>;
  isPostGrantCollecting?: boolean;
}) {
  const t = getTranslations(useScreen0Language().languageCode);
  const {
    permission: livePermission,
    permissionReady,
    requiresEntitlement,
    requestPermission,
  } = useUsageAccess();
  const [permissionBusy, setPermissionBusy] = useState(false);
  const platform = Capacitor.getPlatform();
  const isAndroid = platform === "android";
  const canRequest = canRequestUsagePermission(
    permissionReady,
    livePermission,
    requiresEntitlement,
    permission === "granted" || permission === "denied" || permission === "unavailable"
      ? permission
      : "unavailable",
    platform,
  );
  const staleClass = isRefreshing ? "opacity-55 transition-opacity duration-200" : undefined;
  const isCurrentPeriod = mode === "month"
    ? startOfMonth(selectedDate) === startOfMonth(todayKey())
    : startOfWeek(selectedDate) === startOfWeek(todayKey());
  const historicalNoData = mode === "day" ? !isViewingToday : !isCurrentPeriod;

  async function handleRequestPermission() {
    setPermissionBusy(true);
    try {
      const state = await requestPermission();
      if (state.status === "granted") await onCollectUsage?.();
    } finally {
      setPermissionBusy(false);
    }
  }

  if (historicalNoData && permission === "granted") {
    const preInstall = isPreInstallationPeriod(mode, selectedDate, earliestDataDate);
    return (
      <section className={`liquid-glass-panel grid gap-3 p-5 ${staleClass ?? ""}`}>
        <h2 className="text-xl font-semibold">
          {preInstall
            ? mode === "day"
              ? t.today.preInstallDayTitle
              : mode === "week"
                ? t.today.preInstallWeekTitle
                : t.today.preInstallMonthTitle
            : mode === "day"
              ? t.today.noSnapshotDayTitle
              : mode === "week"
                ? t.today.noSnapshotWeekTitle
                : t.today.noSnapshotMonthTitle}
        </h2>
        <p className="text-sm leading-6 text-[oklch(72%_0.025_155)]">
          {preInstall
            ? mode === "day"
              ? t.today.preInstallDayBody
              : mode === "week"
                ? t.today.preInstallWeekBody
                : t.today.preInstallMonthBody
            : mode === "day"
              ? t.today.noSnapshotDayBody
              : mode === "week"
                ? t.today.noSnapshotWeekBody
                : t.today.noSnapshotMonthBody}
        </p>
        <Link to="/today" className="secondary-action mt-1 inline-flex min-h-[44px] w-full items-center justify-center text-center">
          {t.common.backToToday}
        </Link>
      </section>
    );
  }

  const needsPermission = permission !== "granted";

  // Post-grant collect/retry in flight: say we are reading data instead of the
  // misleading "no usage recorded" (Android can lag right after the grant).
  if (!needsPermission && isPostGrantCollecting && isViewingToday) {
    return (
      <section className={`liquid-glass-panel grid gap-3 p-5 ${staleClass ?? ""}`} aria-busy>
        <div className="flex items-center gap-2.5">
          <span className="relative flex h-2.5 w-2.5" aria-hidden>
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[oklch(74%_0.11_155)] opacity-60" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[oklch(74%_0.11_155)]" />
          </span>
          <h2 className="text-xl font-semibold">{t.today.collectingUsageTitle}</h2>
        </div>
        <p className="text-sm leading-6 text-[oklch(72%_0.025_155)]">
          {isAndroid ? t.today.collectingUsageBodyAndroid : t.today.collectingUsageBody}
        </p>
      </section>
    );
  }

  const restrictedSettingsLikely = isAndroid && livePermission?.restrictedSettingsLikely === true;
  const permissionBody = needsPermission
    ? isAndroid
      ? restrictedSettingsLikely
        ? t.today.permissionNeededBodyAndroidRestricted
        : t.today.permissionNeededBodyAndroid
      : t.today.permissionNeededBody
    : emptyUsageBody(reason, t.today, isAndroid);
  const ctaLabel = isAndroid
    ? permissionBusy
      ? t.permission.openingUsageAccess
      : t.today.openUsageAccess
    : permissionBusy
      ? t.permission.requestingAccess
      : t.permission.requestScreenTimeAccess;

  return (
    <section className={`liquid-glass-panel grid gap-3 p-5 ${staleClass ?? ""}`}>
      <h2 className="text-xl font-semibold">{needsPermission ? t.today.permissionNeededTitle : t.today.noUsageRecorded}</h2>
      <p className="text-sm leading-6 text-[oklch(72%_0.025_155)]">
        {permissionBody}
      </p>
      {needsPermission && restrictedSettingsLikely ? (
        <button
          type="button"
          className="primary-action mt-1 w-full"
          onClick={() => void openAndroidAppInfo()}
        >
          {t.today.openAppInfo}
        </button>
      ) : null}
      {needsPermission && canRequest ? (
        <button
          type="button"
          className={`${restrictedSettingsLikely ? "secondary-action" : "primary-action"} mt-1 w-full disabled:opacity-60`}
          disabled={permissionBusy}
          onClick={() => void handleRequestPermission()}
        >
          {ctaLabel}
        </button>
      ) : needsPermission ? (
        <Link to="/settings" className="primary-action mt-1 w-full text-center">
          {t.common.openSettings}
        </Link>
      ) : null}
    </section>
  );
}

function emptyUsageBody(
  reason: string | undefined,
  todayCopy: TranslationStructure["today"],
  isAndroid = false,
) {
  if (isAndroid) {
    if (reason === "usage_events_unavailable") return todayCopy.reasonUsageEventsUnavailableAndroid;
    if (reason === "apps_not_visible") return todayCopy.reasonAppsNotVisibleAndroid;
    return todayCopy.reasonDefaultNoDataAndroid;
  }
  if (reason === "data_access_not_granted") return todayCopy.permissionDeniedHint;
  if (reason === "ios_data_export_requires_26_4") return todayCopy.reasonIosDataExportRequires264;
  if (reason === "data_export_unavailable") return todayCopy.reasonDataExportUnavailable;
  if (reason === "activity_data_fetch_failed") return todayCopy.reasonActivityDataFetchFailed;
  if (reason === "extension_snapshot_missing") return todayCopy.reasonExtensionSnapshotMissing;
  if (reason === "report_extension_not_invoked") return todayCopy.reasonReportExtensionNotInvoked;
  if (reason === "report_view_not_mounted") return todayCopy.reasonReportViewNotMounted;
  return todayCopy.reasonDefaultNoData;
}

function AppEntityLabel({
  entity,
  categories,
}: {
  entity: TodayEntity;
  categories: TranslationStructure["categories"];
}) {
  if (entity.isCategory) return todayEntityLabel(entity, { categories });
  return (
    <AppFriendlyName
      displayName={entity.displayName}
      appIdentifier={entity.appIdentifier}
      category={entity.category}
    />
  );
}

const TOP_ENTITIES_ROW_CLASS = "tap-row grid grid-cols-[auto_1fr_auto] items-center gap-3";
const TOP_ENTITIES_DRAWER_TITLE_ID = "top-entities-all-title";
const TODAY_APP_ICON_SIZE = 18;
const TODAY_APP_ICON_RENDER_SIZE = 27;

function topEntitiesSectionCopy(
  viewMode: ViewMode,
  metric: RingMetric,
  todayCopy: TranslationStructure["today"],
  all = false,
): { title: string; ariaViewAll: string } {
  if (viewMode === "category") {
    if (metric === "opens") {
      return all
        ? { title: todayCopy.allCategoryActivations, ariaViewAll: todayCopy.viewAllCategoryActivationsAria }
        : { title: todayCopy.topCategoryActivations, ariaViewAll: todayCopy.viewAllCategoryActivationsAria };
    }
    return all
      ? { title: todayCopy.allCategories, ariaViewAll: todayCopy.viewAllCategoriesAria }
      : { title: todayCopy.topCategories, ariaViewAll: todayCopy.viewAllCategoriesAria };
  }
  if (metric === "opens") {
    return all
      ? { title: todayCopy.allActivations, ariaViewAll: todayCopy.viewAllActivationsAria }
      : { title: todayCopy.topActivations, ariaViewAll: todayCopy.viewAllActivationsAria };
  }
  return all
    ? { title: todayCopy.allApps, ariaViewAll: todayCopy.viewAllAppsAria }
    : { title: todayCopy.topApps, ariaViewAll: todayCopy.viewAllAppsAria };
}

function TopEntityRowContent({
  entity,
  metric,
  averageMode = false,
}: {
  entity: TodayEntity;
  metric: RingMetric;
  averageMode?: boolean;
}) {
  const { languageCode } = useScreen0Language();
  const t = getTranslations(languageCode);
  const valueClass = averageMode ? "today-data-value-secondary" : "text-[oklch(72%_0.025_155)]";
  return (
    <>
      <AppIcon
        displayName={entity.displayName}
        appIdentifier={entity.appIdentifier}
        category={entity.category}
        color={entity.color ?? "#7ab89a"}
        size={TODAY_APP_ICON_SIZE}
        iconSize={TODAY_APP_ICON_RENDER_SIZE}
        isCategory={entity.isCategory}
      />
      <div className="min-w-0">
        <div className="truncate font-medium">
          <AppEntityLabel entity={entity} categories={t.categories} />
        </div>
        {!entity.isCategory ? (
          <div className="truncate text-xs text-[oklch(72%_0.025_155)]">
            {appCategoryDisplayLabel(entity.category, t, entity.appIdentifier)}
          </div>
        ) : null}
      </div>
      <span className={`mono text-sm ${valueClass}`}>
        {metric === "opens"
          ? formatActivationCountForDisplay(entity.opens ?? 0, averageMode ? "averaged" : "integer")
          : formatDuration(
              averageMode ? Math.round(entity.durationSeconds) : entity.durationSeconds,
              languageCode,
            )}
      </span>
    </>
  );
}

function TopEntityRow({
  entity,
  index,
  rowClassName,
  content,
  onTap,
  onLongPress,
}: {
  entity: TodayEntity;
  index: number;
  rowClassName: string;
  content: ReactNode;
  onTap?: (index: number) => void;
  onLongPress?: (entity: TodayEntity) => void;
}) {
  const { longPressHandlers } = useLongPress({
    onTap: onTap ? () => onTap(index) : undefined,
    onLongPress: onLongPress ? () => onLongPress(entity) : undefined,
    disabled: !onTap && !onLongPress,
  });

  return (
    <button
      type="button"
      data-top-entity-row=""
      className={`${rowClassName} w-full bg-transparent text-left transition-opacity active:opacity-70`}
      {...longPressHandlers}
      onKeyDown={(event) => {
        if (!onTap) return;
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onTap(index);
        }
      }}
    >
      {content}
    </button>
  );
}

function TopEntitiesAllDrawer({
  drawer,
  onClose,
}: {
  drawer: { entities: TodayEntity[]; metric: RingMetric; viewMode: ViewMode; averageMode: boolean } | null;
  onClose: () => void;
}) {
  const t = getTranslations(useScreen0Language().languageCode);
  const open = drawer !== null;
  const entities = drawer?.entities ?? [];
  const metric = drawer?.metric ?? "time";
  const viewMode = drawer?.viewMode ?? "app";
  const averageMode = drawer?.averageMode ?? false;
  const { title } = topEntitiesSectionCopy(viewMode, metric, t.today, true);

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      labelledBy={TOP_ENTITIES_DRAWER_TITLE_ID}
      maxHeight="80svh"
    >
      <div className="flex h-[80svh] flex-col">
        <div className="px-4 pt-4 pb-1">
          <h2 id={TOP_ENTITIES_DRAWER_TITLE_ID} className="text-lg font-semibold text-[oklch(92%_0.02_150)]">
            {title}
          </h2>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-2">
          <div className="rules-row-list px-4">
            {entities.map((entity) => (
              <div key={entity.entityKey} className={TOP_ENTITIES_ROW_CLASS}>
                <TopEntityRowContent entity={entity} metric={metric} averageMode={averageMode} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </BottomSheet>
  );
}

function TopEntities({
  entities,
  metric,
  viewMode,
  averageMode = false,
  showPlaceholder = false,
  onEntityTap,
  onEntityLongPress,
  onViewMore,
}: {
  entities: TodayEntity[];
  metric: RingMetric;
  viewMode: ViewMode;
  averageMode?: boolean;
  showPlaceholder?: boolean;
  onEntityTap?: (index: number) => void;
  onEntityLongPress?: (entity: TodayEntity) => void;
  onViewMore?: () => void;
}) {
  const t = getTranslations(useScreen0Language().languageCode);
  if (entities.length === 0 && !showPlaceholder) return null;
  const isPlaceholder = showPlaceholder && entities.length === 0;
  const rows: TodayEntity[] = isPlaceholder
    ? Array.from({ length: 3 }, (_, index) => ({
        entityKey: `placeholder-${index}`,
        displayName: "\u00a0",
        durationSeconds: 0,
        isCategory: false,
      }))
    : entities.slice(0, 5);
  const showViewMore = !isPlaceholder && entities.length > 5 && onViewMore;
  const { title, ariaViewAll } = topEntitiesSectionCopy(viewMode, metric, t.today);

  return (
    <section className="grid gap-2">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-base font-semibold">{title}</h2>
        {showViewMore ? (
          <button
            type="button"
            className="min-h-8 shrink-0 px-2 text-xs font-semibold text-[oklch(72%_0.09_155)]"
            onClick={onViewMore}
            aria-label={ariaViewAll}
          >
            {t.today.viewMore}
          </button>
        ) : null}
      </div>
      <div className="rules-row-list px-4">
        {rows.map((entity, index) => {
          const content = isPlaceholder ? (
            <>
              <div className="size-3 rounded-full bg-[oklch(28%_0.02_160)]" />
              <span className="h-4 rounded bg-[oklch(28%_0.02_160)]" />
              <span className="mono h-4 w-12 rounded bg-[oklch(28%_0.02_160)]" />
            </>
          ) : (
            <TopEntityRowContent entity={entity} metric={metric} averageMode={averageMode} />
          );
          const key = entity.entityKey;
          return (onEntityTap || onEntityLongPress) && !isPlaceholder ? (
            <TopEntityRow
              key={key}
              entity={entity}
              index={index}
              rowClassName={TOP_ENTITIES_ROW_CLASS}
              content={content}
              onTap={onEntityTap}
              onLongPress={onEntityLongPress}
            />
          ) : (
            <div key={key} className={TOP_ENTITIES_ROW_CLASS}>
              {content}
            </div>
          );
        })}
      </div>
    </section>
  );
}

// Today accent green. No single "ring color" exists (the ring is multi-segment),
// so the aggregated line uses the app's brand green — the same fallback used for
// app colors elsewhere in this tab.
const TODAY_ACCENT = "#7ab89a";

const deviceFocusSeriesRef = queryRef<
  { anonymousDeviceId: string; date: string; mode: PeriodMode },
  { mode: PeriodMode; points: FocusSeriesPoint[]; hasOpens: boolean }
>("usage:getDeviceFocusSeries");

// The "Day/Week/Month shape" section: the per-app focus line chart, but
// aggregated across all apps for the period via getDeviceFocusSeries.
function DayShapeChart({
  selectedDate,
  mode,
  metric,
  isRefreshing = false,
}: {
  selectedDate: string;
  mode: PeriodMode;
  metric: RingMetric;
  isRefreshing?: boolean;
}) {
  const t = getTranslations(useScreen0Language().languageCode);
  const anonymousDeviceId = useDeviceId();
  const seriesReal = useQuery(deviceFocusSeriesRef, { anonymousDeviceId, date: selectedDate, mode });
  const series = useMarketingDemoOverlay(
    "usage:getDeviceFocusSeries",
    { anonymousDeviceId, date: selectedDate, mode },
    seriesReal,
  );
  const staleClass = isRefreshing ? "opacity-55 transition-opacity duration-200" : undefined;
  return (
    <section className={`section-band section-band-compact grid gap-3${staleClass ? ` ${staleClass}` : ""}`}>
      <h2 className="text-base font-semibold">{mode === "week" ? t.today.weekShape : mode === "month" ? t.today.monthShape : t.today.dayShape}</h2>
      {series === undefined ? (
        <div
          className="w-full animate-pulse rounded-md"
          style={{ height: 150, background: "oklch(24% 0.02 165)" }}
          aria-hidden
        />
      ) : (
        <>
          <FocusSeriesChart
            key={metric}
            points={series.points}
            mode={mode}
            metric={metric}
            color={TODAY_ACCENT}
            hasOpens={series.hasOpens}
          />
          {!series.hasOpens ? (
            <p className="px-1 text-[11px] leading-4" style={{ color: "oklch(52% 0.016 155)" }}>
              {mode === "month"
                ? t.ring.activationsNotTrackedMonth
                : mode === "week"
                  ? t.ring.activationsNotTrackedWeek
                  : t.ring.activationsNotTrackedDay}
            </p>
          ) : null}
        </>
      )}
    </section>
  );
}
